import { useNavigate, useParams } from "react-router-dom";
import { performanceService } from "../../api/performanceService";
import BackLink from "../../components/ui/BackLink";
import { formatManilaDateTime } from "../../utils/manilaTime";
import { cx } from "../../utils/classNames";
import {
  getLatestReturn,
  getPersonName,
  hasAnyAnswer,
  REVIEWER_STATUS,
} from "./performanceUtils";
import { useEvaluation } from "./usePerformanceQueries";
import QueryState from "./components/QueryState";
import EvaluationHeader from "./components/EvaluationHeader";
import EvaluationEditor from "./components/EvaluationEditor";
import EvaluationResults from "./components/EvaluationResults";
import ActivityTimeline from "./components/ActivityTimeline";
import ui from "../../components/ui/ui.module.css";

const RateEvaluationView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const query = useEvaluation(id);

  return (
    <div className={ui.page}>
      <div className={ui.pageHeader}>
        <div>
          <BackLink
            fallback="/me/evaluations-to-rate"
            label="Evaluations to rate"
          />
          <h4 className="page-title">
            {query.data
              ? `${getPersonName(query.data.employee)} · ${query.data.periodLabel}`
              : "Evaluation"}
          </h4>
        </div>
      </div>

      <QueryState query={query} loadingText="Loading evaluation…">
        {(evaluation) => {
          const latestReturn = getLatestReturn(evaluation.activities);
          const showSelf =
            evaluation.template?.allowSelfAssessment &&
            hasAnyAnswer(evaluation.selfResponses);

          return (
            <>
              <EvaluationHeader
                evaluation={evaluation}
                statusMap={REVIEWER_STATUS}
                showEmployee
              />

              {evaluation.status === "PENDING_SELF" && (
                <div className={cx(ui.alert, ui.alertGray)}>
                  Waiting for the employee's self-assessment. You can rate once
                  they submit it.
                </div>
              )}

              {evaluation.permissions?.canEditEvaluation ? (
                <>
                  {latestReturn && (
                    <div
                      className={cx(ui.alert, ui.alertDanger, ui.alertColumn)}
                    >
                      <strong>
                        Returned
                        {latestReturn.stepLabel &&
                          ` by ${latestReturn.stepLabel}`}{" "}
                        on {formatManilaDateTime(latestReturn.createdAt)}
                      </strong>
                      <span>“{latestReturn.remarks}”</span>
                    </div>
                  )}

                  <EvaluationEditor
                    key={evaluation.id}
                    evaluation={evaluation}
                    initialResponses={evaluation.responses}
                    selfResponses={
                      showSelf ? evaluation.selfResponses : undefined
                    }
                    showScores
                    save={(responses) =>
                      performanceService.saveResponses(evaluation.id, responses)
                    }
                    submit={() => performanceService.submit(evaluation.id)}
                    submitLabel="Submit to employee"
                    confirmTitle="Submit evaluation?"
                    confirmMessage="The employee sees the result right away. You can't edit it unless HR sends it back after a contest."
                    successMessage="Evaluation submitted. The employee can now see the result and agree or contest."
                    onSubmitted={() => navigate("/me/evaluations-to-rate")}
                  />
                </>
              ) : (
                evaluation.status !== "PENDING_SELF" &&
                hasAnyAnswer(evaluation.responses) && (
                  <EvaluationResults evaluation={evaluation} />
                )
              )}

              <ActivityTimeline activities={evaluation.activities} />
            </>
          );
        }}
      </QueryState>
    </div>
  );
};

export default RateEvaluationView;
